import { getMemberAccessToken } from '../auth/supabase';

const CACHE_PREFIX = 'smfd-offline-inventory-v1';

function memberScope() {
  if (typeof window === 'undefined') return 'member';
  return String(window.__SMFD_MEMBER_ID__ || 'member').trim() || 'member';
}

function catalogKey() {
  return `${CACHE_PREFIX}:${memberScope()}:catalog`;
}

function assetKey(assetId) {
  return `${CACHE_PREFIX}:${memberScope()}:asset:${String(assetId || '').trim().toUpperCase()}`;
}

export function supportsOfflineInventory(assetId) {
  return /^(LM-PACK|TRAUMA-BAG|O2-BAG|AED)-/i.test(String(assetId || ''));
}

function safeRead(key) {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(window.localStorage?.getItem(key) || 'null');
  } catch (_) {
    return null;
  }
}

function safeWrite(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(key, JSON.stringify(value));
  } catch (_) {
    // Offline cache is best-effort if storage is full or unavailable.
  }
}

export function cacheOfflineInventory(assetIds, payload) {
  const ids = [...new Set((assetIds || []).map(value => String(value || '').trim().toUpperCase()).filter(Boolean))];
  const catalog = Array.isArray(payload?.catalog) ? payload.catalog : [];
  const expirations = Array.isArray(payload?.expirations) ? payload.expirations : [];
  const cachedAt = new Date().toISOString();

  safeWrite(catalogKey(), { catalog, cachedAt });
  ids.forEach(assetId => {
    safeWrite(assetKey(assetId), {
      assetId,
      expirations: expirations.filter(row => String(row?.setId || row?.assetId || '').trim().toUpperCase() === assetId),
      cachedAt,
    });
  });
}

export function readOfflineInventory(assetId) {
  const id = String(assetId || '').trim().toUpperCase();
  if (!id) return null;
  const catalog = safeRead(catalogKey());
  const asset = safeRead(assetKey(id));
  if (!catalog?.catalog || !asset) return null;
  return {
    catalog: Array.isArray(catalog.catalog) ? catalog.catalog : [],
    expirations: Array.isArray(asset.expirations) ? asset.expirations : [],
    cachedAt: String(asset.cachedAt || catalog.cachedAt || ''),
  };
}

export function readOfflineCatalog() {
  const cached = safeRead(catalogKey());
  return Array.isArray(cached?.catalog) ? cached.catalog : [];
}

export async function prefetchOfflineInventory(assetIds) {
  const ids = [...new Set((assetIds || [])
    .map(value => String(value || '').trim().toUpperCase())
    .filter(value => value && supportsOfflineInventory(value)))];
  if (!ids.length || typeof navigator !== 'undefined' && navigator.onLine === false) return false;

  const token = await getMemberAccessToken();
  if (!token) return false;
  const response = await fetch(`/api/member-tracking?mode=inventory-asset&assetIds=${encodeURIComponent(ids.join(','))}`, {
    cache: 'no-store',
    headers: { Authorization: `Bearer ${token}` },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || 'Unable to cache bag inventory for offline use.');
  cacheOfflineInventory(ids, payload);
  return true;
}
